import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function AboutPreview() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-charcoal py-20 md:py-28 lg:py-32">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
          {/* Left: Portrait */}
          <div
            className={`lg:w-[40%] transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative">
              <div className="w-full aspect-square bg-dark-card rounded-lg overflow-hidden">
                <img
                  src="/images/sitting.JPG"
                  alt="Jeff Dumè in a professional setting"
                  loading="eager"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Polaroid Overlap */}
              <div className="absolute -bottom-3 -left-3 w-24 h-28 md:w-32 md:h-40 bg-dark-card rounded-lg border border-white/10 p-1.5 transform -rotate-2 hover:rotate-0 transition-transform duration-500 z-10">
                <img
                  src="/images/hca-award.jpg"
                  alt="Jeff Dumè receiving an HCA award"
                  loading="eager"
                  className="w-full h-full object-cover object-top rounded"
                />
              </div>
              {/* AI-assisted badge */}
              <div className="absolute -top-3 -right-3 w-28 h-28 md:w-36 md:h-36 bg-gold rounded-lg flex flex-col items-center justify-center z-10">
                <span className="text-charcoal text-xs font-bold uppercase tracking-wider text-center leading-tight px-1">AI-assisted</span>
                <span className="text-charcoal text-xs font-bold uppercase tracking-wider text-center leading-tight px-1 mt-0.5">Never AI-decisive</span>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div
            className={`lg:w-[60%] transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-4">
              About Jeff Dum&eacute;
            </p>
            <h2 className="text-white text-2xl md:text-3xl lg:text-display-sm font-bold leading-tight mb-6 font-display">
              Building Better Workforce Decisions
            </h2>

            <div className="space-y-5 text-white/70 text-sm md:text-base leading-relaxed">
              <p>
                Jeff Dum&eacute; is the founder of BuildFit AI&trade; and Mainframe Solutions&trade;, where he works with organizations seeking to improve hiring outcomes, strengthen operational systems, and scale with greater confidence.
              </p>
              <p>
                Working across construction, infrastructure, engineering, healthcare, technology, and public sector organizations, Jeff kept seeing the same pattern: inconsistent evaluations, subjective hiring, competing definitions of success. Those gaps compound. They affect retention, execution, and long-term performance.
              </p>
              <p>
                That pattern led to BuildFit AI&trade;, a platform that structures how organizations evaluate talent, reducing mis-hires, improving retention, and making hiring decisions more consistent.
              </p>
              <p>
                He holds executive certifications in AI Governance from Oxford&rsquo;s Sa&iuml;d Business School and AI &amp; Law from Lund University.
              </p>
              <p>
                He believes organizations grow when people, systems, and decisions are aligned.
              </p>
              <p>
                He believes operational discipline is the foundation of confident growth.
              </p>
            </div>

            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-8 text-gold font-semibold text-sm hover:text-gold-light transition-colors whitespace-nowrap"
            >
              Read Full Story
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}